import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { HiOutlinePlus, HiOutlineTrash } from "react-icons/hi";

import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "components/ui/select";
import DateTimePicker from "../../components/DateTimePicker";

import {
  getOppositionPrepare,
  saveOppositionPrepare,
  OPPOSITION_CLASS_NUMBERS,
  type OppositionPrepareData,
} from "../../api/opposition";

export const OPPOSITION_PREPARE_FORM_ID = "opposition-prepare-opposition-form";

const LABEL_CLASS = "text-[12px] font-normal leading-5 text-[#222222]";
const INPUT_CLASS =
  "w-full min-h-[40px] md:h-9 md:min-h-0 px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border";
const TEXTAREA_CLASS =
  "w-full min-h-[64px] px-2.5 py-2 text-[12px] leading-5 text-[#222222] placeholder:text-[#6D6D6D] bg-white border border-[#EEEEEE] rounded-[6px] focus:outline-none focus:ring-1 focus:ring-[#222] box-border resize-y";
const REQ = <span className="text-red-500 ml-0.5">*</span>;

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

function parseDateStr(s: string | Date | null | undefined): Date | null {
  if (!s) return null;
  if (s instanceof Date) return isNaN(s.getTime()) ? null : s;
  const m = (s as string).match(/^(\d{4})-(\d{2})-(\d{2})/);
  if (m) {
    const d = new Date(Number(m[1]), Number(m[2]) - 1, Number(m[3]));
    return isNaN(d.getTime()) ? null : d;
  }
  const d = new Date(s as string);
  return isNaN(d.getTime()) ? null : d;
}

function toISODate(d: Date | null): string | null {
  if (!d) return null;
  const y = d.getFullYear();
  const mo = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${mo}-${day}`;
}

function formatDisplayDate(s: string | Date | null | undefined): string {
  const d = parseDateStr(s as string | null);
  if (!d) return "—";
  return d.toLocaleDateString("en-IN", { day: "2-digit", month: "short", year: "numeric" });
}

let varCounter = 0;
function makeVarId() { return `var-${++varCounter}-${Date.now()}`; }

let secCounter = 0;
function makeSecId() { return `sec-${++secCounter}-${Date.now()}`; }

// ---------------------------------------------------------------------------
// Local row types
// ---------------------------------------------------------------------------

type MarkVariationRow = {
  rowId: string;
  mark: string;
  classNumber: string;
  applicationNo: string;
};

type ObjectionSectionRow = {
  rowId: string;
  sectionHeading: string;
  sectionText: string;
};

type ConflictingMarkEdit = {
  id: string;
  applicationDate: Date | null;
  journalNo: string;
};

// ---------------------------------------------------------------------------
// Props
// ---------------------------------------------------------------------------

type Props = {
  leadId?: string | null;
  onSuccess?: (leadId: string) => void;
};

// ---------------------------------------------------------------------------
// Component
// ---------------------------------------------------------------------------

export default function OppositionPrepareOppositionView({ leadId, onSuccess }: Props) {
  const [loading, setLoading] = useState<boolean>(!!leadId);
  const [saving, setSaving] = useState(false);
  const loadedRef = useRef(false);

  const [errors, setErrors] = useState<Set<string>>(new Set());
  const hasError = (key: string) => errors.has(key);
  const eb = (key: string) => (errors.has(key) ? " !border-red-500" : "");
  const clearError = (key: string) =>
    setErrors((prev) => {
      if (!prev.has(key)) return prev;
      const next = new Set(prev);
      next.delete(key);
      return next;
    });

  // Read-only data
  const [data, setData] = useState<OppositionPrepareData | null>(null);

  // Editable: per conflicting mark (21t, 21u)
  const [markEdits, setMarkEdits] = useState<ConflictingMarkEdit[]>([]);

  // Editable: lead-level
  const [operatingOutsideIndia, setOperatingOutsideIndia] = useState<boolean | null>(null);
  const [dateOfOpposition, setDateOfOpposition] = useState<Date | null>(null);

  // Editable: dynamic lists
  const [markVariations, setMarkVariations] = useState<MarkVariationRow[]>([]);
  const [objectionSections, setObjectionSections] = useState<ObjectionSectionRow[]>([]);

  // ---------------------------------------------------------------------------
  // Load
  // ---------------------------------------------------------------------------

  useEffect(() => {
    if (!leadId || loadedRef.current) return;
    loadedRef.current = true;
    let cancelled = false;
    setLoading(true);

    getOppositionPrepare(leadId)
      .then((d) => {
        if (cancelled) return;
        setData(d);

        setMarkEdits(
          d.conflictingMarks.map((cm) => ({
            id: cm.id,
            applicationDate: parseDateStr(cm.applicationDate),
            journalNo: cm.journalNo ?? "",
          }))
        );

        setOperatingOutsideIndia(d.operatingOutsideIndia);
        setDateOfOpposition(parseDateStr(d.dateOfOpposition));

        setMarkVariations(
          d.markVariations.length > 0
            ? d.markVariations.map((v) => ({
                rowId: makeVarId(),
                mark: v.mark,
                classNumber: v.classNumber != null ? String(v.classNumber) : "",
                applicationNo: v.applicationNo ?? "",
              }))
            : []
        );

        setObjectionSections(
          d.objectionSections.length > 0
            ? d.objectionSections.map((s) => ({
                rowId: makeSecId(),
                sectionHeading: s.sectionHeading,
                sectionText: s.sectionText ?? "",
              }))
            : []
        );
      })
      .catch((err) => {
        if (cancelled) return;
        toast.error(err instanceof Error ? err.message : "Failed to load prepare opposition data");
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [leadId]);

  // ---------------------------------------------------------------------------
  // Helpers
  // ---------------------------------------------------------------------------

  const updateMarkEdit = (id: string, updates: Partial<ConflictingMarkEdit>) => {
    setMarkEdits((prev) => prev.map((e) => (e.id === id ? { ...e, ...updates } : e)));
  };

  const addMarkVariation = () =>
    setMarkVariations((prev) => [...prev, { rowId: makeVarId(), mark: "", classNumber: "", applicationNo: "" }]);

  const removeMarkVariation = (rowId: string) =>
    setMarkVariations((prev) => prev.filter((r) => r.rowId !== rowId));

  const updateMarkVariation = (rowId: string, updates: Partial<MarkVariationRow>) =>
    setMarkVariations((prev) => prev.map((r) => (r.rowId === rowId ? { ...r, ...updates } : r)));

  const addObjectionSection = () =>
    setObjectionSections((prev) => [...prev, { rowId: makeSecId(), sectionHeading: "", sectionText: "" }]);

  const removeObjectionSection = (rowId: string) =>
    setObjectionSections((prev) => prev.filter((r) => r.rowId !== rowId));

  const updateObjectionSection = (rowId: string, updates: Partial<ObjectionSectionRow>) =>
    setObjectionSections((prev) => prev.map((r) => (r.rowId === rowId ? { ...r, ...updates } : r)));

  // ---------------------------------------------------------------------------
  // Validation
  // ---------------------------------------------------------------------------

  const validate = (): Set<string> => {
    const next = new Set<string>();

    markVariations.forEach((row, i) => {
      if (!row.mark.trim()) next.add(`var-${i}-mark`);
    });

    objectionSections.forEach((row, i) => {
      if (!row.sectionHeading.trim()) next.add(`sec-${i}-heading`);
    });

    return next;
  };

  // ---------------------------------------------------------------------------
  // Submit
  // ---------------------------------------------------------------------------

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!leadId || saving) return;

    const validationErrors = validate();
    if (validationErrors.size > 0) {
      setErrors(validationErrors);
      toast.error("Please fill in all required fields.");
      return;
    }

    setSaving(true);
    try {
      const saved = await saveOppositionPrepare(leadId, {
        operatingOutsideIndia: operatingOutsideIndia,
        dateOfOpposition: toISODate(dateOfOpposition),
        conflictingMarks: markEdits.map((e) => ({
          conflictingMarkId: e.id,
          applicationDate: toISODate(e.applicationDate),
          journalNo: e.journalNo || null,
        })),
        markVariations: markVariations.map((v) => ({
          mark: v.mark,
          classNumber: v.classNumber ? Number(v.classNumber) : undefined,
          applicationNo: v.applicationNo || undefined,
        })),
        objectionSections: objectionSections.map((s) => ({
          sectionHeading: s.sectionHeading,
          sectionText: s.sectionText || undefined,
        })),
      });

      setData(saved);
      setErrors(new Set());
      toast.success("Prepare Opposition saved successfully.");
      onSuccess?.(leadId);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : "Failed to save prepare opposition");
    } finally {
      setSaving(false);
    }
  };

  // ---------------------------------------------------------------------------
  // Early returns
  // ---------------------------------------------------------------------------

  if (!leadId) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12" style={{ fontFamily: "Inter, sans-serif" }}>
        <p className="text-sm text-[#6D6D6D]">Please complete the previous steps to access this step.</p>
      </div>
    );
  }

  if (loading) {
    return (
      <div className="flex flex-col gap-[22px] w-full max-w-[1085px] py-12" style={{ fontFamily: "Inter, sans-serif" }}>
        <p className="text-sm text-[#6D6D6D]">Loading...</p>
      </div>
    );
  }

  const applicant = data?.applicant;

  // ---------------------------------------------------------------------------
  // Render
  // ---------------------------------------------------------------------------

  return (
    <form
      id={OPPOSITION_PREPARE_FORM_ID}
      onSubmit={handleSubmit}
      className="flex flex-col gap-5 w-full max-w-[1085px]"
      style={{ fontFamily: "Inter, sans-serif" }}
    >
      {/* Applicant Info — read-only */}
      <div className="flex flex-col gap-5 bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <p className="text-[14px] font-semibold text-[#101111]">Our Mark Details</p>
        <div className="bg-[#F7F7F7] rounded-[8px] p-4 md:p-5 flex flex-col gap-3 w-full">
          {(
            [
              { label: "Mark", value: applicant?.mark || "" },
              { label: "Applicant", value: applicant?.applicant || "" },
              { label: "Applicant Address", value: applicant?.applicantAddress || "" },
              { label: "Goods / Services", value: applicant?.goodsServices || "", multiline: true },
              {
                label: "GS Type A",
                value: applicant?.gsTypeA
                  ? applicant.gsTypeA.charAt(0).toUpperCase() + applicant.gsTypeA.slice(1)
                  : "",
              },
            ] as Array<{ label: string; value: string; multiline?: boolean }>
          ).map(({ label, value, multiline }) => (
            <div
              key={label}
              className="flex flex-col md:flex-row md:items-baseline gap-1 md:gap-4"
            >
              <span className="text-xs font-normal leading-5 text-[#6D6D6D] md:w-[220px] md:shrink-0">
                {label}
              </span>
              <span
                className={`text-xs font-normal leading-5 break-words ${
                  multiline ? "whitespace-pre-wrap" : ""
                } ${value ? "text-[#222222]" : "text-[#6D6D6D]"}`}
              >
                {value || "—"}
              </span>
            </div>
          ))}
        </div>
      </div>

      {/* Conflicting Applications */}
      <div className="flex flex-col gap-5 bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <div className="flex items-center justify-between flex-wrap gap-2">
          <p className="text-[14px] font-semibold text-[#101111]">Conflicting Applications</p>
          <div className="flex items-center gap-4 text-[12px] text-[#6D6D6D]">
            <span>
              No. of Conflicting Applications:{" "}
              <span className="font-medium text-[#222]">{data?.noOfConflictingApplications ?? "—"}</span>
            </span>
            <span>
              Applications to be Opposed:{" "}
              <span className="font-medium text-[#222]">{data?.noOfApplicationsToBeOpposed ?? "—"}</span>
            </span>
          </div>
        </div>

        {data?.conflictingMarks.length === 0 && (
          <p className="text-[12px] text-[#6D6D6D]">No conflicting applications to display.</p>
        )}

        {data?.conflictingMarks && data.conflictingMarks.length > 0 && (
          <div className="rounded-[6px] border border-[#EEEEEE]">
            <table className="w-full table-fixed border-collapse text-[12px] leading-5">
              <colgroup>
                <col style={{ width: "16%" }} />
                <col style={{ width: "13%" }} />
                <col style={{ width: "6%" }} />
                <col style={{ width: "18%" }} />
                <col style={{ width: "16%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "10%" }} />
                <col style={{ width: "11%" }} />
              </colgroup>
              <thead>
                <tr className="bg-[#F7F7F7] border-b border-[#EEEEEE]">
                  {[
                    "Conflicting Mark",
                    "Application No.",
                    "Class",
                    "Name of Proprietor",
                    "Place of Proprietor",
                    "User Date",
                    "Journal Date",
                    "Last Date of Filing",
                  ].map((col) => (
                    <th
                      key={col}
                      className="px-3 py-2.5 text-left text-[#6D6D6D] font-medium border-r border-[#EEEEEE] last:border-r-0 break-words"
                    >
                      {col}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {data.conflictingMarks.map((cm, idx) => {
                  const edit = markEdits.find((e) => e.id === cm.id);
                  return (
                    <tr key={cm.id} className="border-b border-[#EEEEEE] last:border-b-0 hover:bg-[#FAFAFA]">
                      <td className="px-3 py-2.5 text-[#222] border-r border-[#EEEEEE] break-words">{cm.conflictingMark || <span className="text-[#AAAAAA]">—</span>}</td>
                      <td className="px-3 py-2.5 text-[#222] border-r border-[#EEEEEE] break-words">{cm.applicationNo || <span className="text-[#AAAAAA]">—</span>}</td>
                      <td className="px-3 py-2.5 text-[#222] border-r border-[#EEEEEE] break-words">{cm.classOfMark ?? <span className="text-[#AAAAAA]">—</span>}</td>
                      <td className="px-3 py-2.5 text-[#222] border-r border-[#EEEEEE] break-words">{cm.nameOfProprietor || <span className="text-[#AAAAAA]">—</span>}</td>
                      <td className="px-3 py-2.5 text-[#222] border-r border-[#EEEEEE] break-words">{cm.placeOfProprietor || <span className="text-[#AAAAAA]">—</span>}</td>
                      <td className="px-3 py-2.5 text-[#222] border-r border-[#EEEEEE] break-words">{formatDisplayDate(cm.userDate)}</td>
                      <td className="px-3 py-2.5 text-[#222] border-r border-[#EEEEEE] break-words">{formatDisplayDate(cm.journalDate)}</td>
                      <td className="px-3 py-2.5 text-[#222] border-r border-[#EEEEEE] break-words">{formatDisplayDate(cm.lastDateOfFilingOpposition)}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}

        {data?.conflictingMarks && data.conflictingMarks.length > 0 && (
          <div className="flex flex-col gap-3">
            <p className="text-[13px] font-semibold text-[#101111]">Application Details</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
              {data.conflictingMarks.map((cm) => {
                const edit = markEdits.find((e) => e.id === cm.id);
                if (!edit) return null;
                return (
                  <div
                    key={cm.id}
                    className="flex flex-col gap-3 p-4 rounded-[8px] border border-[#EEEEEE] bg-[#FAFAFA]"
                  >
                    <p className="text-[12px] font-semibold text-[#222]">
                      {cm.conflictingMark || "—"}
                      {cm.applicationNo ? (
                        <span className="ml-1 font-normal text-[#6D6D6D]">({cm.applicationNo})</span>
                      ) : null}
                    </p>
                    <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                      <div className="flex flex-col gap-1">
                        <label className={LABEL_CLASS}>Application Date</label>
                        <DateTimePicker
                          value={edit.applicationDate}
                          onChange={(d) => updateMarkEdit(cm.id, { applicationDate: d })}
                          placeholder="Select Date"
                          buttonClassName="px-[10px] py-2 rounded-[6px] text-[12px] w-full"
                        />
                      </div>
                      <div className="flex flex-col gap-1">
                        <label className={LABEL_CLASS}>Journal No.</label>
                        <input
                          type="text"
                          value={edit.journalNo}
                          onChange={(e) => updateMarkEdit(cm.id, { journalNo: e.target.value })}
                          placeholder="Text Here"
                          className={INPUT_CLASS}
                        />
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* Opposition Details */}
      <div className="flex flex-col gap-5 bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <p className="text-[14px] font-semibold text-[#101111]">Opposition Details</p>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
          <div className="flex flex-col gap-1.5">
            <span className={LABEL_CLASS}>Operating outside India</span>
            <div className="flex flex-wrap gap-x-5 gap-y-2 rounded-[6px]">
              {[
                { label: "Yes", value: true },
                { label: "No", value: false },
              ].map((opt) => (
                <label
                  key={opt.label}
                  className="flex cursor-pointer items-center gap-2 text-[12px] font-normal leading-5 text-[#222222] min-h-[44px] md:min-h-0 touch-manipulation"
                >
                  <input
                    type="radio"
                    name="operatingOutsideIndia"
                    checked={operatingOutsideIndia === opt.value}
                    onChange={() => setOperatingOutsideIndia(opt.value)}
                    className="w-4 h-4 accent-[#222222] cursor-pointer"
                  />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          <div className="flex flex-col gap-1">
            <label className={LABEL_CLASS}>Date of Opposition</label>
            <DateTimePicker
              value={dateOfOpposition}
              onChange={(d) => { setDateOfOpposition(d); clearError("dateOfOpposition"); }}
              placeholder="Select Date"
              buttonClassName="px-[10px] py-2 rounded-[6px] text-[12px]"
              error={hasError("dateOfOpposition")}
            />
          </div>
        </div>
      </div>

      {/* List of Variations of Marks */}
      <div className="flex flex-col gap-5 bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <div className="flex items-center justify-between">
          <p className="text-[14px] font-semibold text-[#101111]">
            List of Variations of Marks
            <span className="ml-2 text-[12px] font-normal text-[#6D6D6D]">({markVariations.length})</span>
          </p>
          <button
            type="button"
            onClick={addMarkVariation}
            disabled={saving}
            className="flex items-center gap-1.5 text-[12px] font-medium text-[#222] border border-[#EEEEEE] rounded-[6px] px-3 py-1.5 hover:bg-[#F7F7F7] disabled:opacity-50"
          >
            <HiOutlinePlus className="w-3.5 h-3.5" strokeWidth={2} />
            Add Variation
          </button>
        </div>

        {markVariations.length === 0 && (
          <p className="text-[12px] text-[#6D6D6D]">No variations added. Click "Add Variation" to add one.</p>
        )}

        <div className="flex flex-col gap-4">
          {markVariations.map((row, idx) => (
            <div key={row.rowId} className="flex flex-col gap-3 border border-[#EEEEEE] rounded-[10px] p-4 bg-[#FAFAFA]">
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-semibold text-[#222]">Variation #{idx + 1}</span>
                <button
                  type="button"
                  onClick={() => removeMarkVariation(row.rowId)}
                  disabled={saving}
                  className="text-[#E53E3E] hover:text-red-700 disabled:opacity-50"
                  title="Remove"
                >
                  <HiOutlineTrash className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div className="flex flex-col gap-1">
                  <label className={LABEL_CLASS}>Mark{REQ}</label>
                  <input
                    type="text"
                    value={row.mark}
                    onChange={(e) => {
                      updateMarkVariation(row.rowId, { mark: e.target.value });
                      clearError(`var-${idx}-mark`);
                    }}
                    placeholder="Text Here"
                    className={INPUT_CLASS + eb(`var-${idx}-mark`)}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className={LABEL_CLASS}>Class</label>
                  <Select
                    value={row.classNumber}
                    onValueChange={(v) => updateMarkVariation(row.rowId, { classNumber: v })}
                  >
                    <SelectTrigger className={INPUT_CLASS}>
                      <SelectValue placeholder="Select Class" />
                    </SelectTrigger>
                    <SelectContent>
                      {OPPOSITION_CLASS_NUMBERS.map((n) => (
                        <SelectItem key={n} value={String(n)}>{n}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="flex flex-col gap-1">
                  <label className={LABEL_CLASS}>Application No.</label>
                  <input
                    type="text"
                    value={row.applicationNo}
                    onChange={(e) => updateMarkVariation(row.rowId, { applicationNo: e.target.value })}
                    placeholder="Text Here"
                    className={INPUT_CLASS}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* Objection Sections */}
      <div className="flex flex-col gap-5 bg-white border border-[#EEEEEE] rounded-[10px] p-5">
        <div className="flex items-center justify-between">
          <p className="text-[14px] font-semibold text-[#101111]">
            Objection Sections
            <span className="ml-2 text-[12px] font-normal text-[#6D6D6D]">({objectionSections.length})</span>
          </p>
          <button
            type="button"
            onClick={addObjectionSection}
            disabled={saving}
            className="flex items-center gap-1.5 text-[12px] font-medium text-[#222] border border-[#EEEEEE] rounded-[6px] px-3 py-1.5 hover:bg-[#F7F7F7] disabled:opacity-50"
          >
            <HiOutlinePlus className="w-3.5 h-3.5" strokeWidth={2} />
            Add Section
          </button>
        </div>

        {objectionSections.length === 0 && (
          <p className="text-[12px] text-[#6D6D6D]">No objection sections added. Click "Add Section" to add one.</p>
        )}

        <div className="flex flex-col gap-4">
          {objectionSections.map((row, idx) => (
            <div key={row.rowId} className="flex flex-col gap-3 border border-[#EEEEEE] rounded-[10px] p-4 bg-[#FAFAFA]">
              <div className="flex items-center justify-between">
                <span className="text-[13px] font-semibold text-[#222]">Section #{idx + 1}</span>
                <button
                  type="button"
                  onClick={() => removeObjectionSection(row.rowId)}
                  disabled={saving}
                  className="text-[#E53E3E] hover:text-red-700 disabled:opacity-50"
                  title="Remove"
                >
                  <HiOutlineTrash className="w-4 h-4" strokeWidth={1.5} />
                </button>
              </div>
              <div className="flex flex-col gap-4">
                <div className="flex flex-col gap-1">
                  <label className={LABEL_CLASS}>Objection under Section Heading{REQ}</label>
                  <input
                    type="text"
                    value={row.sectionHeading}
                    onChange={(e) => {
                      updateObjectionSection(row.rowId, { sectionHeading: e.target.value });
                      clearError(`sec-${idx}-heading`);
                    }}
                    placeholder="e.g. Objection under Section 9(1)"
                    className={INPUT_CLASS + eb(`sec-${idx}-heading`)}
                  />
                </div>
                <div className="flex flex-col gap-1">
                  <label className={LABEL_CLASS}>Text of Objection under Section Heading</label>
                  <textarea
                    value={row.sectionText}
                    onChange={(e) => updateObjectionSection(row.rowId, { sectionText: e.target.value })}
                    placeholder="Text Here"
                    className={TEXTAREA_CLASS}
                    rows={3}
                  />
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </form>
  );
}
